import { useTranslate } from "#src/hooks";
import {
  blue,
  cyan,
  green,
  magenta,
  orange,
  purple,
  red,
} from "@ant-design/colors";
import {
  Card,
  ColorPickerProps,
  Flex,
  Form,
  FormInstance,
  Typography,
} from "antd";
import {
  ColorPicker,
  SquareColorPicker,
} from "components/ColorPicker/ColorPicker";
import { AggregationColor } from "components/ColorPicker/colorTypes/";
import { useAnalytics } from "hooks/useAnalytics";
import { useAntTheme } from "hooks/useAntTheme/";
import { PaletteResponse } from "pages/media/types";
import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { AnalyticsEvents } from "types/analytics";

const colorPickerProps: ColorPickerProps = {
  showText: true,
  format: "hex",
  size: "middle",
};

const PREVIEW_BLOCK_SIZE = 40;

export const ColorPickerCard = ({
  colorKey,
  allowClear,
  form,
  setColors,
  value,
  required,
  style,
}: {
  colorKey: string;
  allowClear?: boolean;
  form: FormInstance;
  setColors: React.Dispatch<React.SetStateAction<Partial<PaletteResponse>>>;
  value?: ColorPickerProps["value"];
  required?: boolean;
  style?: React.CSSProperties;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { trackEvent } = useAnalytics();

  // Local state for the preview color (updates on onChange/onChangeComplete)
  const [previewColor, setPreviewColor] = useState<
    ColorPickerProps["value"] | null
  >(value);

  const getCurrentFormColors = () => ({
    primary_color: form.getFieldValue(["colors", "primary_color"]) || undefined,
    secondary_color:
      form.getFieldValue(["colors", "secondary_color"]) || undefined,
  });

  const resetColor = (colorKey: string) => {
    form.setFieldValue(["colors", colorKey], null);
    setPreviewColor(null);
    setColors((prev) => ({
      ...prev,
      [colorKey]: null,
    }));
  };

  const handleColorChange = (color: string) => {
    form.setFieldValue(["colors", colorKey], color);
    setColors((prev) => ({
      ...prev,
      [colorKey]: color,
    }));
    trackEvent(AnalyticsEvents.BrandKitColorChange, {
      brandkit_color_name: color,
    });
  };

  const genPresets = useMemo(
    () => [
      // empty label to hide the collapsible
      { label: "", colors: red },
      { label: "", colors: green },
      { label: "", colors: cyan },
      { label: "", colors: blue },
      { label: "", colors: purple },
      { label: "", colors: orange },
      { label: "", colors: magenta },
    ],
    []
  );

  const commonColorPickerProps = {
    value: previewColor,
    palette: getCurrentFormColors(),
    presets: genPresets,
    styles: {
      popupOverlayInner: { minWidth: "calc(min(520px, 80vw))" },
    },
    allowClear: allowClear,
    onClear: () => resetColor(colorKey),
    onChangeComplete: (color: AggregationColor) => {
      if (color.cleared) {
        resetColor(colorKey);
        return;
      }
      setPreviewColor(color.toHexString());
      handleColorChange(color.toHexString());
    },
  };

  return (
    <StyledFormItem
      initialValue={value}
      name={["colors", colorKey]}
      required={false}
      rules={
        required ? [{ required: true, message: t("forms.requiredField") }] : []
      }
    >
      <Card
        style={{ display: "flex", flexDirection: "column" }}
        styles={{
          body: {
            flexGrow: 1,
            padding: 24,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <Typography.Title
          level={4}
          style={{ fontWeight: 400, margin: "0 0 8px", fontSize: "17px" }}
        >
          {t(`media.brandkit.create.${colorKey}.title`)}
          {required && (
            <span
              aria-hidden="true"
              style={{ color: "#ff4d4f", marginLeft: 4 }}
            >
              *
            </span>
          )}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ paddingBlockEnd: 12 }}>
          {t(`media.brandkit.create.${colorKey}.description`)}
        </Typography.Text>
        <Flex gap={12} align="center" style={{ width: "100%" }}>
          <StyledColorPickerWrapper>
            <ColorPicker
              {...colorPickerProps}
              {...commonColorPickerProps}
              size="middle"
              showText={(color) =>
                color.cleared ? (
                  <Typography.Text type="secondary">
                    {t(`media.brandkit.detail.defaultColor`)}
                  </Typography.Text>
                ) : (
                  color.toHexString().toUpperCase()
                )
              }
              style={{
                background: theme.colorBorderSecondary,
                padding: 4,
                flex: 1,
                justifyContent: "start",
                alignItems: "center",
                ...style,
              }}
            />
          </StyledColorPickerWrapper>
          <StyledSquareColorPickerWrap>
            <SquareColorPicker
              {...commonColorPickerProps}
              showText={false}
              disabledAlpha
            />
          </StyledSquareColorPickerWrap>
        </Flex>
      </Card>
    </StyledFormItem>
  );
};

const StyledFormItem = styled(Form.Item)`
  margin: 0;
  flex: 1;
  min-width: 0;
  height: 100%;

  .ant-row {
    height: 100%;
    justify-content: space-between;
  }

  > .ant-col {
    height: 100%;
  }

  .ant-form-item-control-input,
  .ant-form-item-control-input-content,
  .ant-card {
    height: 100%;
  }

  .ant-card-body > span {
    flex-grow: 1;
  }
`;

const StyledColorPickerWrapper = styled.div`
  flex: 1;
  .ant-color-picker-trigger {
    .ant-color-picker-trigger-text {
      font-family: "Inter Variable", sans-serif;
    }
  }
  .ant-color-picker-trigger > .ant-color-picker-color-block,
  .ant-color-picker-trigger > .ant-color-picker-clear {
    display: none !important;
  }
`;

const previewBlockStyles = css`
  width: ${PREVIEW_BLOCK_SIZE}px !important;
  height: ${PREVIEW_BLOCK_SIZE}px !important;
  border-radius: ${PREVIEW_BLOCK_SIZE / 6}px;
`;

const StyledSquareColorPickerWrap = styled.div`
  .ant-color-picker-trigger {
    ${previewBlockStyles}
    min-width: ${PREVIEW_BLOCK_SIZE}px;
    min-height: ${PREVIEW_BLOCK_SIZE}px;

    > .ant-color-picker-color-block {
      ${previewBlockStyles}

      > .ant-color-picker-color-block-inner {
        ${previewBlockStyles}
      }
    }

    > .ant-color-picker-clear {
      ${previewBlockStyles}
      inset: 0;
      border: none !important;
      outline: inherit;

      &::after {
        width: ${Math.round(PREVIEW_BLOCK_SIZE * 1.4)}px !important;
      }
    }
  }
`;
